import { Component, Input, SimpleChanges } from '@angular/core';
import { IColumnDefinition } from '@common/models/column-definition';
import { IOverviewTileModel } from '@feature/overview/models/overview-tile-model';
import { TableFacadeService } from '@feature/_app_modules/shared/table/services/table-facade.service';
import { SelectItem } from 'primeng/api/selectitem';
import { ListerConfigurationHttpService } from '../../../../services/lister-configuration-http.service';
import { combineLatest, tap } from 'rxjs';
import { ColumnConfigControlItem, ListerColumnConfig, ProductListerConfigDto } from './lister-configuration.types';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Component({
  selector: 'lister-configuration',
  templateUrl: './lister-configuration.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ListerConfigurationComponent
    }
  ]
})
export class ListerConfigurationComponent implements ControlValueAccessor {

  @Input() listerPages: IOverviewTileModel[];
  @Input() productName: string;
  selectableListers: SelectItem[];
  selectedListerConfig: ColumnConfigControlItem[] = [];
  allListerConfigs: ListerColumnConfig[] = [];
  disabled = false;
  touched = false;

  constructor(private readonly tableFacadeService: TableFacadeService,
    private readonly listerConfigService: ListerConfigurationHttpService) { }

  onChange = (allListerConfigs) => {};

  onTouched = () => {};
  
  writeValue(allListerConfigs: ListerColumnConfig[]): void {
    this.allListerConfigs = allListerConfigs;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  ngOnChanges(changes: SimpleChanges){
    if(changes['listerPages'] && this.listerPages != null){
      this.selectableListers = this.mapToSelectItemModel(this.listerPages);
      this.getAllListerConfigs(this.selectableListers);
    }
  }

  updateListerColumns(selectedListerChange: SelectItem){
    if(selectedListerChange.value && this.allListerConfigs.length > 0){
      this.selectedListerConfig = this.allListerConfigs.filter(x => x.ListerName == selectedListerChange.value)[0]?.ColumnConfig ?? [];
    }
  }

 getAllListerConfigs(selectableListers: SelectItem[]){
  selectableListers.forEach(x => {
    const listerConfig = this.listerConfigService.getConfig(x.value, this.productName);
    const allColumns = this.tableFacadeService.getAllColumns(x.value);
    
    combineLatest([listerConfig, allColumns]).pipe(
      tap(([config, columns]) => {
        const columnConfigs = this.mapToColumnConfigControlItemObject(columns, config);
        this.allListerConfigs.push(
          {
            ListerName: x.value, 
            ColumnConfig: columnConfigs
          } as ListerColumnConfig
        );
      })
    ).subscribe();
  })
 }

  mapToSelectItemModel(items: IOverviewTileModel[]): SelectItem[] {
    if(!items) return;
    let results: SelectItem[] = [];
    items.forEach(t2 => results.push({ label: t2.Label, value: t2.EntityName }));
    this.removeHome(results);
    return results;
  }

  removeHome(selectableTiles: SelectItem[]){
    const homeIndex = selectableTiles.findIndex((item) => {
      return item.value == null
    });

    if(homeIndex !== -1){
      selectableTiles.splice(homeIndex, 1);
    }
  }

  handleAllowedColumnChange(section: ColumnConfigControlItem){
    if(!section.Allowed && section.Default){
      this.selectedListerConfig.filter(x => x.ColDef.Field == section.ColDef.Field)[0].Default = false;
    }
    this.onChange(this.allListerConfigs)
  }

  handleDefaultColumnChange(section: ColumnConfigControlItem){
    if(!section.Allowed && section.Default){
      this.selectedListerConfig.filter(x => x.ColDef.Field == section.ColDef.Field)[0].Allowed = true;
    }
    this.onChange(this.allListerConfigs)
  }

  handleAllowAllColumnsChecked(): boolean{
    return (this.selectedListerConfig || []).every(x => x.Allowed);
  }

  handleAllowAllChange(evt) {
    try {
      (this.selectedListerConfig || []).forEach(x => x.Allowed = evt.target.checked);
      if(!evt.target.checked){
        (this.selectedListerConfig || []).forEach(x => x.Default = false);
      }
      this.onChange(this.allListerConfigs);
    } catch (e) {
      console.error(e);
    }
  }

  handleDefaultAllColumnsChecked(): boolean{
    return (this.selectedListerConfig || []).every(x => x.Default);
  }

  handleDefaultAllChange(evt) {
    try {
      (this.selectedListerConfig || []).forEach(x => x.Default = evt.target.checked);
      if(evt.target.checked){
        (this.selectedListerConfig || []).forEach(x => x.Allowed = true);
      }
      this.onChange(this.allListerConfigs);
    } catch (e) {
      console.error(e);
    }
  }

  mapToColumnConfigControlItemObject(allListerColumns: IColumnDefinition[], config: ProductListerConfigDto): ColumnConfigControlItem[]{
    
    return allListerColumns.map((columnDefinition: IColumnDefinition) => {
      const column = new ColumnConfigControlItem();
      column.ColDef= columnDefinition,
      column.Allowed = config.AllowedListerColumns.some((x) => x == columnDefinition.Field);
      column.Default = config.DefaultListerColumns.some((x) => x == columnDefinition.Field);
      return column;
    });
  }
}